import React, { useState, useMemo, useEffect, useRef } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Button, MenuPopup } from '@prosolve/common-components-ui'
import { FaPlus, FaBook, FaShareAlt, FaEdit } from 'react-icons/fa';
import SessionList from '../../../SessionList/SessionList';
import EditScheduleNotesModal from './EditScheduleNotesModal';
import EditScheduleModal from './EditScheduleModal';
import SetScheduleFromBlueprintModal from './SetScheduleFromBlueprintModal';
import useAttemptSessionDataMigration from '../../../../hooks/useAttemptSessionDataMigration';
import { checkHasEditEventPermissions, getClientSubdomain, getIsProsolveSubdomain, isStarConsultation } from '../../../../utils';
import useIsMobileView from "../../../../hooks/useIsMobileView";
import useShareEvent from '../../EventMainContent/hooks/useShareEvent';
import ScheduleBlock from '../../EventLeftHud/ScheduleBlock';
import ShareProgramModal from '../../EventMainContent/ShareProgramModal';
import ShareLogisticsButton from '../../EventMainContent/ShareLogisticsButton';

const SessionDayBlock = styled.div`
  background: ${props => props.$selected ? '#F3FBFF' : '#ffffff'};
  color: ${props => props.$selected ? '#0280c6' : '#565656'};
  padding: 10px 30px;
  border: ${props => props.$selected ? '1px solid #0280c6' : '1px solid #c4c4c4'};
  border-radius: 5px;
  font-size: 16px;
  word-wrap: break-word;
  cursor: pointer;
`;

export default ({
  eventData = {},
  onReloadData
}) => {
  const hasEditEventPermissions = checkHasEditEventPermissions()
  const isMobileView = useIsMobileView();

  const clientSubdomain = getClientSubdomain()
  const isStarSubdomain = (clientSubdomain === 'star')
  const isProsolveSubdomain = getIsProsolveSubdomain()

  const hasCheckedInitialLoad = useRef(false);

  const [isLoading, setIsLoading] = useState(true)
  const [sessions, setSessions] = useState([])
  const [displayFilters, setDisplayFilters] = useState({})
  const [isEditScheduleMenuOpen, setIsEditScheduleMenuOpen] = useState(false)
  const [editSessionsObj, setEditSessionsObj] = useState()
  const [sessionsFromBlueprintObj, setSessionsFromBlueprintObj] = useState()
  const [isShareScheduleModalOpen, setIsShareScheduleModalOpen] = useState()
  const [isEditScheduleNotesOpen, setIsEditScheduleNotesOpen] = useState()

  const initSessionFilters = { eventId: eventData.id };
  const [sessionFilters] = useState(initSessionFilters)

  const { getShareEventLink, getShareEventLinkData, getShareLogisticsLinkData } = useShareEvent()
  const shareEventLink = getShareEventLink(eventData)

  const { 
    isMigrationAttemptOccuring,
    attemptedManualSessionLoad,
    attemptInitialSessionFromEventTemplateLoad,
  } = useAttemptSessionDataMigration()

  const {
    timeZone,
    scheduleNotes,
  } = eventData

  const sessionDays = useMemo(() => {
    const uniqueDates = []
    sessions.forEach(({ date }) => {
      if (uniqueDates.indexOf(date) < 0) {
        uniqueDates.push(date);
      }
    })
    return uniqueDates
      .map((date) => moment(date, 'YYYY-MM-DD'))
      .sort((a, b) => a - b)
  }, [sessions]);

  useEffect(() => {
    if (sessionDays[0]) {
      setDisplayFilters({
        date: sessionDays[0].format('YYYY-MM-DD')
      })
    }
  }, [sessionDays])

  const onSessionsChange = (sessions) => {
    setSessions(sessions)
  }

  const onSessionsLoadingChange = (isLoading) => {
    setIsLoading(isLoading)
  }

  const onSessionDaySelected = (sessionDay) => {
    setDisplayFilters({
      date: sessionDay.format('YYYY-MM-DD')
    })
  }

  const onEditScheduleSelect = async (val) => {
    if (val === 'Calendar View') {
      onEditSchedule()
    } else if (val === 'Logistics View') {
      const isStarConsultationSession = isStarConsultation(eventData)
      const { url } = (
        isStarConsultationSession ? 
          await getShareEventLinkData(eventData, true) : 
          await getShareLogisticsLinkData(eventData, true)
      )
      window.open(`http://${url}`, '_blank')
    }
  }

  const onEditSchedule = () => {
   setEditSessionsObj(sessions)
  }

  const onSetScheduleFromBlueprint = () => {
    setSessionsFromBlueprintObj(sessions)
  }

  const OnEditScheduleNotesSuccess = () => {
    onReloadData?.()
  } 

  useEffect(() => {
    if (!isLoading && !hasCheckedInitialLoad.current && !attemptedManualSessionLoad) {
      hasCheckedInitialLoad.current = true;
      
      if (sessions.length === 0) {
        attemptInitialSessionFromEventTemplateLoad(eventData.id, () => window.dispatchEvent(new CustomEvent('reloadSessions', {})))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptedManualSessionLoad, isLoading, sessions])

  return (
    <div 
      style={{ 
        // padding: '20px'
      }}
    >
      {
        isShareScheduleModalOpen &&
        <ShareProgramModal
          url={shareEventLink}
          text='To share the schedule for this event, go to:'
          onClose={() => setIsShareScheduleModalOpen(false)}
        />
      }
      {
        Boolean(isEditScheduleNotesOpen) &&
        <EditScheduleNotesModal
          eventData={eventData}
          onClose={() => setIsEditScheduleNotesOpen()}
          onSuccess={() => OnEditScheduleNotesSuccess()}
        />
      }
      {
        Boolean(editSessionsObj) &&
        <EditScheduleModal
          sessions={editSessionsObj}
          eventId={eventData.id}
          onClose={() => setEditSessionsObj(undefined)}
          onSuccess={() => setEditSessionsObj(undefined)}
        />
      }
      {
        Boolean(sessionsFromBlueprintObj) &&
        <SetScheduleFromBlueprintModal
          initialSessions={sessionsFromBlueprintObj}
          eventId={eventData.id}
          defaultStartDate={eventData.startDate}
          onClose={() => setSessionsFromBlueprintObj(undefined)}
          onSuccess={() => setSessionsFromBlueprintObj(undefined)}
        />
      }
      <div style={{
        display: 'flex',
        alignItems: 'center'
      }}>
        <div style={{
          flex: 1,
          color: '#565656',
          fontSize: '24px',
          fontWeight: 'bold',
          padding: '20px'
        }}>
          Program Schedule
        </div>
        {isProsolveSubdomain &&
          <Button
            style={{
              display: 'inline-flex',
              padding: '8px 16px',
              marginRight: '20px'
            }}
            onClick={() => setIsEditScheduleNotesOpen(true)}
          >
            <FaEdit style={{ marginRight: '10px', color: '#0280c6' }} />
            <span style={{ fontWeight: 'bold', color: '#565656' }}>{'Edit Schedule Notes'}</span>
          </Button>
        }
        <Button
          style={{
            display: 'inline-flex',
            padding: '8px 16px',
            marginRight: '20px'
          }}
          onClick={() => setIsShareScheduleModalOpen(true)}
        >
          <FaShareAlt style={{ marginRight: '10px', color: '#0280c6' }} />
          <span style={{ fontWeight: 'bold', color: '#565656' }}>{'Share Schedule'}</span>
        </Button>
        {isStarSubdomain &&
          <ShareLogisticsButton eventData={eventData} />
        }
        {
          hasEditEventPermissions &&
          <Button
            style={{
              display: 'inline-flex',
              position: 'relative',
              padding: '8px 16px',
              marginRight: '20px'
            }}
            //onClick={onEditSchedule}
            onClick={(e) => { e.stopPropagation(); setIsEditScheduleMenuOpen(true) }}
          >
            <FaPlus style={{ marginRight: '10px', color: '#0280c6' }} />
            <span style={{ fontWeight: 'bold', color: '#565656' }}>{sessions.length > 0 ? 'Edit Schedule' : 'Add Schedule'}</span>
            <MenuPopup
              open={isEditScheduleMenuOpen}
              onClose={() => setIsEditScheduleMenuOpen(false)}
              options={[
                'Calendar View',
                'Logistics View'
              ]}
              onSelection={onEditScheduleSelect}
              width={140}
              top={10}
            >
            </MenuPopup>
          </Button>
        }
        {
          hasEditEventPermissions && !isMobileView &&
          <Button
            style={{
              display: 'inline-flex',
              padding: '8px 16px',
              marginRight: '20px'
            }}
            onClick={onSetScheduleFromBlueprint}
          >
            <FaBook style={{ marginRight: '10px', color: '#0280c6' }} />
            <span style={{ fontWeight: 'bold', color: '#565656' }}>{'Copy Schedule'}</span>
          </Button>
        }
      </div>
      {
        !isMobileView &&
        <>
        <div style={{
          display: 'flex',
          padding: '20px',
          columnGap: '20px',
          rowGap: '20px',
          flexWrap: 'wrap'
        }}>
        {
          scheduleNotes
          ?
          <div style={{
            display: 'flex',
            border: '1px solid #dedede',
            borderRadius: '5px',
            background: '#fdfdfd',
            padding: '20px'
          }}>
            {scheduleNotes}
          </div>
          :
          sessionDays.length > 0
          ?
          sessionDays.map((sessionDay, index) => {
            const displayDate = sessionDay.format('ddd, MM/DD/YY');
            const isSelected = displayFilters.date === sessionDay.format('YYYY-MM-DD');
            return (
              <SessionDayBlock 
                key={displayDate}
                $selected={isSelected}
                onClick={() => onSessionDaySelected(sessionDay)}
              >
                <div style={{ color: isSelected ? '#222222' : '#565656' }}>{`Day ${index + 1}`}</div>
                <div style={{ fontWeight: 'bold', marginTop: '10px' }}>{displayDate}</div>
              </SessionDayBlock>
            )
          })
          :
          <div style={{
            display: 'flex',
            border: '1px solid #dedede',
            borderRadius: '5px',
            background: '#fdfdfd',
            padding: '20px'
          }}>
            {'No sessions have been scheduled for this event.'}
          </div>
        }
        </div>
        {
          Boolean(timeZone) &&
          <div style={{ marginLeft: '20px', color: '#565656' }}>
            {`All times are in the ${timeZone} time zone`}
          </div>
        }
        </>
      }
      {
        !isMigrationAttemptOccuring && !isMobileView &&
        <SessionList filters={sessionFilters} displayFilters={displayFilters} onSessionsChange={onSessionsChange} onSessionsLoadingChange={onSessionsLoadingChange} isStarSubdomain={isStarSubdomain} />
      }
      {
        isMobileView &&
        <ScheduleBlock eventData={eventData} onSessionsChange={onSessionsChange} suppressTitle={true} />
      }
    </div>
  )
}